/**
 * Utility functions for the moderation page
 */

export * from "./conversationUtils"

/**
 * Format a Supabase ISO timestamp (or unix seconds) to a readable date/time string.
 * e.g. "Mar 12, 10:07 AM"
 */
export function formatTimestamp(input: string | number): string {
  let ms: number
  if (typeof input === "string") {
    ms = new Date(input).getTime()
  } else {
    const ts = Number(input)
    if (!Number.isFinite(ts)) return "—"
    ms = ts < 1e12 ? ts * 1000 : ts
  }
  const date = new Date(ms)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

export function truncateMessage(content: string, maxLength: number = 100): string {
  if (!content) return ""
  if (content.length <= maxLength) return content
  return content.slice(0, maxLength) + "..."
}

export function getInitials(name: string): string {
  return name
    .split(" ")
    .map(word => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

const BADGE_BASE = "text-[9px] h-4 px-1 shrink-0 hover:bg-inherit!"
const BADGE_MSG  = "text-[10px] h-4 px-1.5 hover:bg-inherit!"

export const badgeStyles = {
  base:         BADGE_BASE,
  human:        BADGE_BASE,
  bot:          BADGE_BASE,
  sim:          `${BADGE_BASE} border-purple-400 text-purple-600`,
  needsReply:   `${BADGE_BASE} bg-yellow-100 text-yellow-800 border-yellow-400 animate-pulse`,
  assigned:     BADGE_BASE,
  riskCategory: `${BADGE_BASE} max-w-[180px] h-auto py-0.5`,
  highRisk:     `${BADGE_BASE} bg-red-100 text-red-700 border-red-300`,
  lowConfidence:`${BADGE_BASE} bg-amber-100 text-amber-800 border-amber-300`,
  askedHuman:   `${BADGE_BASE} bg-blue-100 text-blue-700 border-blue-300`,
  escalated:    `${BADGE_BASE} bg-red-100 text-red-800 border-red-300`,
  unread:       `${BADGE_BASE} ml-auto`,
  msgBase:      BADGE_MSG,
  msgRiskCategory: `${BADGE_MSG} h-auto py-0.5 bg-purple-100 text-purple-800 border-purple-300 max-w-full`,
  msgEscalated:    `${BADGE_MSG} bg-red-100 text-red-800 border-red-300`,
  msgOutgoing:     `${BADGE_MSG} px-1 bg-primary/15 text-primary border-primary/25`,
  msgIncoming:     `${BADGE_MSG} px-1`,
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null
  return function (...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}
